import React, { useEffect, useState } from "react";
import AdminTable from "../../Components/AdminTable/AdminTable";
import Select from "react-select";
import SearchBar from "../../Components/SearchBar/SearchBar";
import { MDBBtn, MDBCheckbox, MDBIcon, MDBInput, MDBSpinner } from "mdb-react-ui-kit";
import Moment from "react-moment";
import { useNavigate, useParams } from "react-router-dom";
import LoadingIndicator from "../../Components/LoadingIndicator/LoadingIndicator";
import { LondonDate } from "../../Utils/utils";
import Paginator from "../../Components/Paginator/Paginator";
import ApiService from "../../Core/Service/ApiService";
import { ManagerRole, ManagerRoleOptions } from "../../Constants/constants";
import { useContext } from "react";
import CmsContext from "../../Context/CmsContext";


const ManagerList = React.memo(props => {
    const navigate = useNavigate();
    const perPageOptions = [
        {value: 10, label: '10'},
        {value: 20, label: '20'},
        {value: 50, label: '50'},
        {value: 100, label: '100'},
    ]
    const [formData, setFormData] = useState({
        pageNum: 1,
        perPage: 10,
        search: {},
        sort: {}
    })
    const [total, setTotal] = useState(0);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [manager_permissions, setManagerPermissions] = useState([]);
    const cmsContext = useContext(CmsContext);

    useEffect(() => {
        cmsContext.setBreadcrumb([
            { title: cmsContext.currMainMenu?.label ?? ''},
            { title: cmsContext.currSubMenu?.label ?? ''},
            { title: 'List'},
        ])
    }, [])

    const getUsers = async () => {
        setLoading(true);

        const data = {
            ...formData,
            search: JSON.stringify(formData.search),
            sort: JSON.stringify(formData.sort)
        }
        const response = await ApiService.get('/managers', data);
        setTotal(response.data.total)
        setRows(response.data.rows);

        setLoading(false);
    }

    const loadManagerPermissions = async () => {
        const response = await ApiService.get('/manager_permissions/all');
        setManagerPermissions(response.data.rows);
    }

    useEffect(() => {
        getUsers();
    }, [formData])

    useEffect(() => {
        loadManagerPermissions();
    }, []);

    const onPageChanged = (pageNum) => {
        setFormData({
            ...formData,
            pageNum
        })
    }
    const onPerPageChanged = (opt) => {
        setFormData({
            ...formData,
            pageNum: 1,
            perPage: opt.value
        })
    }
    const handleSearch = (search) => {
        setFormData({
            ...formData,
            pageNum: 1,
            search: search
        })
    }
    const handleSort = (sort) => {
        setFormData({
            ...formData,
            pageNum: 1,
            sort: sort
        })
    }

    const onAddBtnClicked = (e) => {
        navigate(`/manager/create`);
    }
    const gotoEdit = (row) => {
        navigate(`/manager/edit/${row.manager_id}`);
    }
    const handleCopy = (row) => {
        navigate(`/manager/copy/${row.manager_id}`);
    }

    const onActiveChange = (e, row) => {
        const data = {
            manager_id: row.manager_id,
            deleted: !e.target.checked
        }

        const message = e.target.checked ? "Are you sure to activate?" : "Are you sure to deactivate?";
        if(window.confirm(message) == false) return;
        
        ApiService.post("/managers/activate", data).then(() => {
            getUsers();
        })
    }

    const renderTbodyTr = (row, rowIndex) => {
        let className = "";

        if(row.deleted == "0") {
            className = "active";
        } else {
            className = "inactive";
        }

        return (
            <tr key={rowIndex} className={`${className}`}>
                { columns.map((col, colIndex) => {
                    const el = renderTdCell(row, col.data ?? col.name, rowIndex, colIndex);
                    if(el) {
                        return <td key={colIndex}>{el}</td>
                    } else {
                        return <td key={colIndex}>{row[col.name]}</td>
                    }
                })}
            </tr>
        )
    }

    const renderTdCell = (row, colname, rowIndex, colIndex) => {
        switch(colname) {
            case 'deleted':
                return <MDBCheckbox checked={row.deleted != "1"} onChange={e=>onActiveChange(e, row)} />
            case 'permission':
                let html = '';
                if(row.role == ManagerRole.CmsUser) {
                    if(manager_permissions?.length > 0) {
                        html = row.permissions?.map(p=>{
                            const mp = manager_permissions.find(mp => mp.value == p);
                            return mp?.label;
                        })?.join(', ');
                    }
                } else {
                    html = 'All';
                }
                return <div className="cursor-pointer" onClick={e=>gotoEdit(row)}>{html}</div>;
            case 'role':
                const mr = ManagerRoleOptions.find(mr=>mr.value == row.role);
                return <div className="cursor-pointer" onClick={e=>gotoEdit(row)}>{mr?.label}</div>;
            case 'action':
                return (
                <div className="d-flex justify-content-end">
                    <MDBBtn color="none" tag="a" onClick={() => gotoEdit(row)} title="Edit User">
                        <MDBIcon fas icon="pencil-alt" size="lg" color="warning" />
                    </MDBBtn>
                    <MDBBtn color="none" tag="a" title="Copy Content" onClick={() => handleCopy(row)}>
                        <MDBIcon far icon='copy' size="lg" className="ms-2" color="warning" />
                    </MDBBtn>
                </div>
                )
            default:
                return <div className="cursor-pointer" onClick={e=>gotoEdit(row)}>{row[colname]}</div>;
        }
    }

    const columns = [
        {name: 'username', title: 'Username', sortable: true, search: {
            enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]
        }}, 
        {name: 'role', title: 'Role', sortable: true, search: {
            enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]
        }},
        {name: 'email', title: 'Email', sortable: true, search: {
            enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]
        }},
        {data: 'organization', name: 'epos_organizations.name', title: 'Organization', sortable: true, search: {
            enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]
        }},
        {name: 'permission', title: 'Permission'},
        {name: 'deleted', title: 'Active', sortable: true, search: {
            enable: true, type: 'select', ops:[{value:'=', label: '='}], options:[{value: '', label: 'All'}, {value: '0', label: 'Active'}, {value: '1', label: 'Deactive'}]
        }},
        {name: 'action', title: 'Action', headerClassName: "d-flex justify-content-end"}
    ];
    return (
    <div className="d-flex flex-column h-100">
        <div className="d-flex align-items-center me-1 mt-2">
            <div className="flex-fluid d-flex align-items-center ps-2">
                <label>Per Page:</label>
                <div className="ms-2" style={{width:100}}>
                    <Select 
                        options={perPageOptions} 
                        value={perPageOptions.find(opt=>opt.value == formData.perPage)} 
                        onChange={onPerPageChanged}
                    />
                </div>
                <label className="ms-4">
                    Found 
                    <span className='fw-bold mx-2' style={{color: '#333'}}>{total}</span> 
                    Matching Results.
                </label>
            </div>

            <div className="d-flex align-items-center">
                <MDBBtn className="ms-4" onClick={onAddBtnClicked}>
                    <MDBIcon icon="plus" />
                    <span className="ms-1">Create User</span>
                </MDBBtn>
            </div>
        </div>

        <div className="flex-fluid mt-3">
            <AdminTable 
                columns={columns} 
                rows={rows} 
                renderTbodyTr={renderTbodyTr} 
                handleSearch={handleSearch} 
                handleSort={handleSort}
                search={formData.search} 
                sort={formData.sort}
            />
        </div>

        <div className="flex-fluid d-flex justify-content-center mt-4">
            <Paginator curPage={formData.pageNum} total={total} perPage={formData.perPage} onPageChanged={onPageChanged} />
        </div>

        <LoadingIndicator show={loading} />
    </div>
    )
});

export default ManagerList;